// useWebSocket — connects on login, reconnects with exponential backoff, fans
// out events to the rest of the app via React Query invalidation + toasts.
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { useStore } from "../store";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "sitroom_token";

const wsUrl = () => {
  const base = BACKEND_URL.replace(/^http/, "ws");
  return `${base}/api/ws`;
};

export function useWebSocket() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const addToast = useStore((s) => s.addToast);
  const [status, setStatus] = useState("idle"); // idle | connecting | connected | reconnecting
  const setWsStatus = useStore((s) => s.setWsStatus);
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    setWsStatus(status);
  }, [status, setWsStatus]);

  useEffect(() => {
    if (!user || user === false) {
      setStatus("idle");
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      return;
    }

    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) return;

      setStatus(retryRef.current === 0 ? "connecting" : "reconnecting");
      const ws = new WebSocket(wsUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(JSON.stringify({ token }));
      };
      ws.onmessage = (e) => {
        let msg;
        try {
          msg = JSON.parse(e.data);
        } catch {
          return;
        }
        const { event, payload } = msg;
        if (event === "WELCOME") {
          retryRef.current = 0;
          setStatus("connected");
          return;
        }
        if (event === "PONG") return;
        handleEvent(event, payload, { qc, addToast });
      };
      ws.onclose = () => {
        wsRef.current = null;
        if (cancelled) return;
        retryRef.current += 1;
        const delay = Math.min(30000, 500 * 2 ** Math.min(retryRef.current, 6));
        setStatus("reconnecting");
        timerRef.current = setTimeout(connect, delay);
      };
      ws.onerror = () => {
        // onclose fires after — backoff lives there
      };
    };

    connect();

    // Ping every 25s to keep the connection alive through proxies
    const ping = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === 1) {
        try {
          wsRef.current.send(JSON.stringify({ event: "PING" }));
        } catch (_) {}
      }
    }, 25000);

    return () => {
      cancelled = true;
      clearInterval(ping);
      if (timerRef.current) clearTimeout(timerRef.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [user, qc, addToast]);

  return { status };
}

function handleEvent(event, payload, { qc, addToast }) {
  const p = payload || {};
  switch (event) {
    case "INCIDENT_CREATED":
      qc.invalidateQueries({ queryKey: ["incidents"] });
      addToast({
        type: "info",
        title: "New incident opened",
        body: `${p.id || ""} · ${p.title || ""}`.trim(),
        link: p.id ? { type: "incident", id: p.id } : undefined,
      });
      break;
    case "INCIDENT_STATUS_CHANGED":
      qc.invalidateQueries({ queryKey: ["incidents"] });
      if (p.incident_id) {
        qc.invalidateQueries({ queryKey: ["incidents", p.incident_id] });
      }
      break;
    case "INCIDENT_ESCALATED":
      qc.invalidateQueries({ queryKey: ["incidents"] });
      addToast({
        type: "s1",
        title: "Incident escalated",
        body: `${p.incident_id || ""} · ${p.reason || ""}`,
        link: p.incident_id ? { type: "incident", id: p.incident_id } : undefined,
      });
      break;
    case "COMMENT_ADDED":
      qc.invalidateQueries({ queryKey: ["incidents"] });
      if (p.incident_id) {
        qc.invalidateQueries({ queryKey: ["incidents", p.incident_id] });
      }
      break;
    case "SLA_WARNING":
      qc.invalidateQueries({ queryKey: ["incidents"] });
      addToast({
        type: "warning",
        title: "SLA Warning · 75%",
        body: `${p.incident_id || ""} · ${p.title || ""}`,
        link: p.incident_id ? { type: "incident", id: p.incident_id } : undefined,
      });
      break;
    case "SLA_BREACH":
      qc.invalidateQueries({ queryKey: ["incidents"] });
      addToast({
        type: "s1",
        title: "SLA BREACH",
        body: `${p.incident_id || ""} · ${p.title || ""}`,
        link: p.incident_id ? { type: "incident", id: p.incident_id } : undefined,
      });
      break;
    case "FMEA_GENERATED":
      qc.invalidateQueries({ queryKey: ["fmeas"] });
      addToast({
        type: "info",
        title: "FMEA suggestion ready",
        body: `From ${p.eight_d_id || ""}`,
        link: p.fmea_id ? { type: "fmea", id: p.fmea_id } : (p.eight_d_id ? { type: "8d", id: p.eight_d_id } : undefined),
      });
      break;
    case "NOTIFICATION_DELIVERED":
      qc.invalidateQueries({ queryKey: ["incidents"] });
      qc.invalidateQueries({ queryKey: ["audit-logs"] });
      break;
    case "MENTION_CREATED":
      qc.invalidateQueries({ queryKey: ["mentions-unread"] });
      qc.invalidateQueries({ queryKey: ["incidents"] });
      addToast({
        type: "info",
        title: "You were mentioned",
        body: `${p.from_user || "Someone"} · ${p.incident_id || ""}`,
        link: p.incident_id ? { type: "incident", id: p.incident_id } : undefined,
      });
      break;
    case "NOTIFICATION_CREATED":
      qc.invalidateQueries({ queryKey: ["inbox"] });
      qc.invalidateQueries({ queryKey: ["inbox-count"] });
      break;
    case "SERVICE_REQUEST_CREATED":
    case "SERVICE_REQUEST_UPDATED":
      qc.invalidateQueries({ queryKey: ["service-requests"] });
      qc.invalidateQueries({ queryKey: ["inbox"] });
      qc.invalidateQueries({ queryKey: ["inbox-count"] });
      break;

    // Iter38/39 — Sorting Module WS events
    case "SORTING_RESULT_CREATED":
    case "SORTING_RESULT_UPDATED":
    case "SORTING_RESULT_IMPORTED":
      qc.invalidateQueries({ queryKey: ["incidents"] });
      qc.invalidateQueries({ queryKey: ["sorting-results", p.incident_id] });
      break;
    case "INCIDENT_TIMELINE_APPENDED":
      qc.invalidateQueries({ queryKey: ["incidents"] });
      if (p.incident_id) qc.invalidateQueries({ queryKey: ["incidents", p.incident_id] });
      break;
    case "SORTING_SUGGESTION": {
      const sug = p.suggestion || {};
      const isWarn = sug.level === "warning";
      addToast({
        type: isWarn ? "warning" : "info",
        title: `✦ SITREP — ${sug.title || "Sorting suggestion"}`,
        body: `${p.incident_id || ""} · ${sug.body || ""}`.trim(),
        link: p.incident_id ? { type: "incident", id: p.incident_id } : undefined,
      });
      break;
    }
    case "PROPOSE_8D_UPDATE":
      addToast({
        type: "info",
        title: "Update 8D D3 (Interim Containment)?",
        body: `${p.incident_id || ""} · ${p.proposal || ""}`,
        link: p.eight_d_id ? { type: "8d", id: p.eight_d_id } : undefined,
        action: p.incident_id ? {
          label: "Review →",
          onClick: () => {
            window.location.href = `/8d?focus=${encodeURIComponent(p.eight_d_id || "")}&incident=${encodeURIComponent(p.incident_id)}`;
          },
        } : undefined,
      });
      break;
    case "PROPOSE_FMEA_UPDATE":
      addToast({
        type: "info",
        title: "Update FMEA Process Controls?",
        body: `${p.incident_id || ""} · ${p.proposal || ""}`,
        link: p.fmea_id ? { type: "fmea", id: p.fmea_id } : undefined,
        action: p.fmea_id ? {
          label: "Review →",
          onClick: () => {
            window.location.href = `/fmea?focus=${encodeURIComponent(p.fmea_id)}&incident=${encodeURIComponent(p.incident_id || "")}`;
          },
        } : undefined,
      });
      break;

    // Iter43-A — TMS Module WS events. During the bidding/negotiation phase
    // (audience='negotiation') only shipper-logistics + invited carriers
    // receive these events from the backend. Once 'TRANSPORT_AGREED' fires,
    // the audience flips to 'broadcast' so production/quality teams see it.
    case "TRANSPORT_REQUEST_CREATED":
      qc.invalidateQueries({ queryKey: ["tms"] });
      qc.invalidateQueries({ queryKey: ["incident-trs", p.incident_id] });
      break;
    case "TRANSPORT_REQUEST_RECEIVED":
      qc.invalidateQueries({ queryKey: ["tms"] });
      addToast({
        type: "info",
        title: "Transport request received",
        body: `${p.transport_request_id || ""} · respond with rate + timing`,
        link: p.transport_request_id ? { type: "tms", id: p.transport_request_id } : undefined,
      });
      break;
    case "TRANSPORT_OFFER_RECEIVED":
      qc.invalidateQueries({ queryKey: ["tms"] });
      qc.invalidateQueries({ queryKey: ["incident-trs", p.incident_id] });
      addToast({
        type: "info",
        title: "New carrier offer",
        body: `${p.transport_request_id || ""} · rate ${p.rate ?? "—"}`,
        link: p.transport_request_id ? { type: "tms", id: p.transport_request_id } : undefined,
      });
      break;
    case "TRANSPORT_OFFER_DECLINED":
      qc.invalidateQueries({ queryKey: ["tms"] });
      qc.invalidateQueries({ queryKey: ["incident-trs", p.incident_id] });
      break;
    case "TRANSPORT_AGREED":
      qc.invalidateQueries({ queryKey: ["tms"] });
      qc.invalidateQueries({ queryKey: ["incident-trs", p.incident_id] });
      addToast({
        type: "success",
        title: "Transport awarded",
        body: `${p.transport_request_id || ""} · ${p.carrier_name || p.carrier_tenant_id || ""} @ ${p.agreed_rate ?? "—"}`,
        link: p.transport_request_id ? { type: "tms", id: p.transport_request_id } : undefined,
      });
      break;
    case "TRANSPORT_AWARDED":
      // Sent specifically to the awarded carrier tenant. Vehicle-data
      // countdown begins now.
      qc.invalidateQueries({ queryKey: ["tms"] });
      addToast({
        type: "success",
        title: "You won this transport",
        body: `${p.transport_request_id || ""} · submit vehicle data before deadline`,
        link: p.transport_request_id ? { type: "tms", id: p.transport_request_id } : undefined,
      });
      break;
    case "TRANSPORT_VEHICLE_DATA":
      qc.invalidateQueries({ queryKey: ["tms"] });
      qc.invalidateQueries({ queryKey: ["incident-trs", p.incident_id] });
      addToast({
        type: "info",
        title: "Vehicle data submitted",
        body: p.transport_request_id || "",
        link: p.transport_request_id ? { type: "tms", id: p.transport_request_id } : undefined,
      });
      break;
    case "TRANSPORT_PICKUP":
      qc.invalidateQueries({ queryKey: ["tms"] });
      qc.invalidateQueries({ queryKey: ["incident-trs", p.incident_id] });
      addToast({
        type: "info",
        title: "Cargo picked up",
        body: p.transport_request_id || "",
        link: p.transport_request_id ? { type: "tms", id: p.transport_request_id } : undefined,
      });
      break;
    case "TRANSPORT_DELIVERED":
      qc.invalidateQueries({ queryKey: ["tms"] });
      qc.invalidateQueries({ queryKey: ["incident-trs", p.incident_id] });
      addToast({
        type: "success",
        title: "Delivery confirmed",
        body: p.transport_request_id || "",
        link: p.transport_request_id ? { type: "tms", id: p.transport_request_id } : undefined,
      });
      break;
    case "TRANSPORT_CANCELLED":
      qc.invalidateQueries({ queryKey: ["tms"] });
      qc.invalidateQueries({ queryKey: ["incident-trs", p.incident_id] });
      addToast({
        type: "warning",
        title: "Transport cancelled",
        body: p.transport_request_id || "",
        link: p.transport_request_id ? { type: "tms", id: p.transport_request_id } : undefined,
      });
      break;
    default:
      break;
  }
}
